/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useContext, useState } from 'react';
import type { MutableRefObject } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { useRetracked } from 'js/lib/retracked';

import { Button, Tag } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

type SkillTagsProps = {
  skillNames?: string[];
};

const MAX_SKILL_TAGS_TO_SHOW = 5;

const styles = {
  skillNamesWrapper: () => css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-200);
  `,
  skillTagsShowMoreButton: css`
    height: 22px;
  `,
};

const SkillTags = ({ skillNames }: SkillTagsProps) => {
  const [showAll, setShowAll] = useState<boolean>(false);
  const { sharedEventingV3Data } = useContext(ProfileContext);

  const retrackedTrack = useRetracked();
  const track = useTracker();

  const btnTrackingName = showAll ? EVENTS.credentialSkillTagsShowLess : EVENTS.credentialSkillTagsShowMore;

  const visibilityTrackingRef: MutableRefObject<HTMLButtonElement | null> = useVisibilityTracker('view_button', {
    button: {
      name: btnTrackingName,
    },
    ...sharedEventingV3Data,
  });

  if (!skillNames || !skillNames.length) return null;

  const skillNamesLength = skillNames.length;
  const skillNamesToRender = showAll ? skillNames : skillNames.slice(0, MAX_SKILL_TAGS_TO_SHOW);

  return (
    <div css={styles.skillNamesWrapper}>
      {skillNamesToRender.map((skillName) => (
        <Tag key={skillName} variant="category">
          {skillName}
        </Tag>
      ))}
      {skillNamesLength > MAX_SKILL_TAGS_TO_SHOW ? (
        /* I wrap the link with a TrackedDiv for retracked visibility tracking since I cannot use tracked components on
        CDS links. It breaks eventing V3's visibility tracking, which relies on an HTML ref. Using component=
        {TrackedButton} on a CDS button causes the ref to refer to the TrackedLink component itself, *not* the
        underlying HTML element, which in turn results in the IntersectionObserver used in `useVisibilityTracker()` to 
        throw an error since we're not referencing an html element. */
        <TrackedDiv
          trackingName={btnTrackingName}
          withVisibilityTracking
          requireFullyVisible={false}
          trackClicks={false}
        >
          <Button
            ref={visibilityTrackingRef}
            variant="ghost"
            size="small"
            onClick={() => {
              retrackedTrack({
                trackingName: btnTrackingName,
                trackingData: {},
                action: 'click',
              });

              track('click_button', {
                button: {
                  name: btnTrackingName,
                },
                ...sharedEventingV3Data,
              });

              setShowAll((prev: boolean) => !prev);
            }}
            css={styles.skillTagsShowMoreButton}
          >
            {showAll ? (
              <FormattedMessage message={_t('Show less')} />
            ) : (
              <FormattedMessage
                message={_t('+ {restNumberOfSkills} more')}
                restNumberOfSkills={skillNamesLength - MAX_SKILL_TAGS_TO_SHOW}
              />
            )}
          </Button>
        </TrackedDiv>
      ) : null}
    </div>
  );
};

export default SkillTags;
